import {
	type AttachmentFileType,
	type AttachmentId,
	attachmentId,
	fileId,
	type SuretyAccountId,
	type SuretyQuoteId,
} from "@inrev/common";
import { accountQueryKey } from "@portal/api/account";
import { bondRequestAttachmentsQueryKey } from "@portal/api/bond-request";
import { useAttachmentUpload } from "@portal/api/bond-request/agent";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";
import { getNameAndExtensionFromFileName } from "../../../utils";

type Options = {
	accountId: SuretyAccountId;
	quoteId: SuretyQuoteId;
};

export const useKeyDocumentUpload = ({ accountId, quoteId }: Options) => {
	const [uploadingTypes, setUploadingTypes] = useState<AttachmentFileType[]>([]);
	const [deletingAttachmentIds, setDeletingAttachmentIds] = useState<Set<AttachmentId>>(
		() => new Set(),
	);

	const invalidateQueryKeys = useMemo(
		() => [bondRequestAttachmentsQueryKey(quoteId), accountQueryKey(accountId)],
		[quoteId, accountId],
	);

	const clearUploading = useCallback((type: AttachmentFileType) => {
		setUploadingTypes((current) => current.filter((t) => t !== type));
	}, []);

	const clearDeleting = useCallback((id: AttachmentId) => {
		setDeletingAttachmentIds((current) => {
			if (!current.has(id)) return current;
			const next = new Set(current);
			next.delete(id);
			return next;
		});
	}, []);

	const { uploadFn, deleteFn } = useAttachmentUpload<AttachmentFileType>(
		`/v2/admin/surety/quotes/${quoteId}/attachments`,
		{
			onUploadSuccess: (_data, args) => {
				for (const upload of args.fileUploads) {
					for (const type of upload.pendingFile.types ?? []) clearUploading(type);
				}
			},
			onUploadError: (_error, args) => {
				for (const upload of args.fileUploads) {
					for (const type of upload.pendingFile.types ?? []) clearUploading(type);
				}
			},
			onDeleteSuccess: (args) => clearDeleting(attachmentId.parse(args.id)),
			onDeleteError: (_error, args) => clearDeleting(attachmentId.parse(args.id)),
		},
		invalidateQueryKeys,
	);

	const upload = useCallback(
		(files: File[], type: AttachmentFileType) => {
			if (files.length === 0) return;
			setUploadingTypes((current) => (current.includes(type) ? current : [...current, type]));
			uploadFn({
				fileUploads: files.map((file) => {
					const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
					return {
						rawFile: file,
						pendingFile: {
							id: fileId.parse(v4()),
							name: fileName,
							extension,
							types: [type],
						},
					};
				}),
			});
		},
		[uploadFn],
	);

	const remove = useCallback(
		(id: AttachmentId) => {
			setDeletingAttachmentIds((current) => {
				if (current.has(id)) return current;
				const next = new Set(current);
				next.add(id);
				return next;
			});
			deleteFn({ id });
		},
		[deleteFn],
	);

	const isUploading = useCallback(
		(type: AttachmentFileType) => uploadingTypes.includes(type),
		[uploadingTypes],
	);

	const isDeleting = useCallback(
		(id: AttachmentId) => deletingAttachmentIds.has(id),
		[deletingAttachmentIds],
	);

	return { upload, remove, isUploading, isDeleting };
};
